// ── Shared pagination ─────────────────────────────────────────────────────────

export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// ── Venue ─────────────────────────────────────────────────────────────────────

export type VenueType =
  | 'RESTAURANT'
  | 'HOTEL'
  | 'WINE_BAR'
  | 'RETAIL'
  | 'PRIVATE'
  | 'OTHER';

export type VenueStatus = 'ACTIVE' | 'INACTIVE';

export interface Venue {
  _id: string;
  name: string;
  shortName?: string;
  type: VenueType;
  status: VenueStatus;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  defaultCurrency: string;
  /** W7A — venue logo metadata. Optional. The image is served via the auth-gated
   *  admin URL stored in `logoUrl`; public surfaces resolve their own URL. */
  logoUrl?: string | null;
  logoStorageKey?: string | null;
  logoOriginalName?: string | null;
  logoMimeType?: string | null;
  logoSizeBytes?: number | null;
  logoUploadedAt?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── VenueLocation ─────────────────────────────────────────────────────────────

export type LocationType =
  | 'CELLAR'
  | 'BAR'
  | 'STORAGE'
  | 'FLOOR'
  | 'FRIDGE'
  | 'OTHER';

export interface VenueLocation {
  _id: string;
  venueId: string;
  name: string;
  type: LocationType;
  description?: string;
  isDefault: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── WineList ──────────────────────────────────────────────────────────────────

export type WineListType =
  | 'FULL'
  | 'BY_THE_GLASS'
  | 'RESERVE'
  | 'EVENT'
  | 'PRIVATE';

export type WineListStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export interface WineList {
  _id: string;
  venueId: string;
  name: string;
  type: WineListType;
  status: WineListStatus;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// ── WineListFormat (W3) ───────────────────────────────────────────────────────

export interface WineListFormat {
  _id: string;
  venueId: string;
  wineListId: string;
  name: string;
  volumeMl: number;
  position: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── WineListItemFormat (W3) ───────────────────────────────────────────────────

export type WineListItemFormatVisibility = 'LIVE' | 'HIDDEN';

/**
 * Joins a WineListItem to a WineListFormat with a price. The GET endpoint may
 * populate `formatId` with the full format object — code reading this type
 * should accept either the id string or the embedded format.
 */
export interface WineListItemFormat {
  _id: string;
  wineListItemId: string;
  formatId: string | WineListFormat;
  price: number;
  visibility: WineListItemFormatVisibility;
  createdAt: string;
  updatedAt: string;
}

// ── WineListSection ───────────────────────────────────────────────────────────

export type WineListSectionVisibility = 'LIVE' | 'HIDDEN';

export interface WineListSection {
  _id: string;
  venueId: string;
  wineListId: string;
  name: string;
  description?: string;
  position: number;
  visibility: WineListSectionVisibility;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── WineListItem ──────────────────────────────────────────────────────────────

export type WineListItemVisibility = 'LIVE' | 'HIDDEN';
export type WineListItemSource = 'MANUAL' | 'IMPORT' | 'AUTO_FROM_RECEIVING';

export interface WineListItem {
  _id: string;
  wineListId: string;
  venueId: string;
  wineId: string;
  /** Link to a specific inventory item — kept for creation flow convenience. Optional. */
  inventoryItemId?: string;
  /** Bottle size for this wine list entry (ml). Own field — independent of inventoryItemId. */
  bottleSize?: number;
  /** Optional assignment to a WineListSection. null/undefined = unsectioned. */
  sectionId?: string | null;
  /** Denormalised snapshot of wine display fields for list rendering */
  wineDisplay?: {
    name: string;
    displayVintage: string;
    producerName?: string;
    regionName?: string;
    color?: string;
  };
  /** @deprecated Renamed to bottlePrice in W3. Mirror-written for legacy reads. */
  listPrice?: number;
  /** Canonical bottle price (W3+). Reads should prefer this, then fall back to listPrice. */
  bottlePrice?: number;
  currency?: string;
  binNumber?: string;
  visibility: WineListItemVisibility;
  source: WineListItemSource;
  notes?: string;
  position?: number;
  active: boolean;
  /** W7C — optional external link surfaced in the customer detail modal. Free-text URL. */
  externalUrl?: string;
  /**
   * W8 (revised) — optional critic scores. Each entry is `{source, score}`.
   * Empty array / undefined = no scores. Validated server-side: max one entry
   * per source, score 50–100.
   */
  scores?: WineListScoreEntry[];
  createdAt: string;
  updatedAt: string;
}

/** W8 — wine list scoring publications surfaced on cards as "JS96", "WA96", etc. */
export type WineListScoreSource = 'JS' | 'WA' | 'WS' | 'AV';
export const WINE_LIST_SCORE_SOURCES: readonly WineListScoreSource[] = ['JS', 'WA', 'WS', 'AV'];
export const WINE_LIST_SCORE_SOURCE_LABELS: Record<WineListScoreSource, string> = {
  JS: 'James Suckling',
  WA: 'Wine Advocate',
  WS: 'Wine Spectator',
  AV: 'Average score',
};
export interface WineListScoreEntry {
  source: WineListScoreSource;
  score: number;
}

// ── Wine list render (admin preview DTO) ──────────────────────────────────────

export type WineListAvailabilityBand =
  | 'IN_STOCK'
  | 'LOW_STOCK'
  | 'OUT_OF_STOCK'
  | 'UNKNOWN';

export interface WineListRenderItemFormat {
  name: string;
  volumeMl: number;
  price: number;
}

export interface WineListRenderItem {
  id: string;
  name: string | null;
  producer: string | null;
  vintage: string | null;
  /** Snapshot region name from wineDisplay — kept for backwards compatibility. */
  region: string | null;
  /** W6.4: country at the root of the wine's region hierarchy (display name, not ISO code). */
  country?: string | null;
  /**
   * W6.4: sub-region names from country root downward,
   * e.g. ["Burgundy", "Côte de Beaune", "Meursault"]. Empty when no hierarchy
   * data is available.
   */
  regionPath?: string[];
  /**
   * W6.4: pre-formatted regionPath for inline display,
   * e.g. "Burgundy · Côte de Beaune · Meursault". Falls back to wineDisplay
   * regionName on the server when no hierarchy is available — clients should
   * just render whichever is present.
   */
  regionDisplay?: string | null;
  color: string | null;
  bottleSize: number | null;
  /** Canonical bottle price (W3+). */
  bottlePrice: number | null;
  /** Legacy alias of bottlePrice — kept for backwards compatibility. */
  price: number | null;
  /** Configured pour formats with prices, sorted by catalog position. Empty if none. */
  formats: WineListRenderItemFormat[];
  currency: string | null;
  notes: string | null;
  /**
   * STAFF-ONLY (admin preview only). Must NOT be exposed in any future
   * public/customer-facing wine list response — binNumber leaks bin location.
   */
  binNumber?: string | null;
  /**
   * STAFF-ONLY (admin preview only). Venue-wide stock total for the linked
   * inventory item, or null when the wine list item is not linked. Exact stock
   * leaks operational state and must NOT be exposed publicly.
   */
  stockQuantity?: number | null;
  /** STAFF-ONLY. Pre-formatted label, e.g. "Stock 3" / "Stock unknown" / "Out of stock". */
  stockLabel?: string | null;
  /** STAFF-ONLY. Coarse band, safe to derive UI styling from. */
  availabilityBand?: WineListAvailabilityBand;
  /**
   * W4: bottle-level availability — Math.floor(stockQuantity), clamped at 0;
   * null when there is no inventory link. Safe to surface to customers (no
   * exact stock disclosed). Format-aware availability (glass, pours) is a
   * future phase.
   */
  bottlesAvailable?: number | null;
  /**
   * W7B: optional external link surfaced in the customer detail modal —
   * e.g. a tasting note, producer page, importer sheet, or press article.
   * Frontend-only for now (mocked in PreviewMock; no backend field yet).
   * Rendered as-is — no validation, no transformation, no scraping.
   */
  externalUrl?: string;
  /** W8 (revised) — optional critic scores rendered as compact badges. */
  scores?: WineListScoreEntry[];
}

export interface WineListRenderSection {
  /** null when this is the synthetic "unsectioned" bucket. */
  id: string | null;
  name: string;
  description: string | null;
  position: number;
  visibility: WineListSectionVisibility;
  items: WineListRenderItem[];
}

export interface WineListRender {
  list: {
    /** Internal id for admin preview; not present in the public DTO (slug only). */
    id?: string;
    /** Slug for the public DTO; not present in the admin preview. */
    slug?: string;
    name: string;
    type: WineListType;
    status?: WineListStatus;
    description: string | null;
    /** W7A — venue display name. Present on both admin + public renders. */
    venueName?: string | null;
    /** W7A — URL for the venue logo image. Auth-gated on admin, public on /m/:slug. */
    venueLogoUrl?: string | null;
  };
  sections: WineListRenderSection[];
}

// ── InventoryLot ──────────────────────────────────────────────────────────────

export type InventoryLotSourceType = 'GOODS_RECEIPT' | 'MANUAL' | 'PRODUCTION' | 'ADJUSTMENT' | 'COUNT_GAIN';

export interface InventoryLot {
  _id: string;
  venueId: string;
  inventoryItemId: string;
  lotNumber: string;
  supplierBatchRef?: string;
  expiryDate?: string;
  receivedDate: string;
  supplierId?: string;
  sourceType: InventoryLotSourceType;
  sourceDocumentId?: string;
  unitCostAtReceipt?: number;
  notes?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ItemLotLocation {
  locationId: string;
  locationName?: string;
  quantity: number;
}

export interface ItemLotsResponse {
  itemId: string;
  lotTracked: boolean;
  lots: Array<InventoryLot & { locations: ItemLotLocation[] }>;
}

// ── InventoryItem ─────────────────────────────────────────────────────────────

export type InventoryItemType = 'WINE' | 'PURCHASED' | 'MANUFACTURED';

export type InventoryItemCategory =
  | 'Wine' | 'Beer' | 'Spirits' | 'Beverage' | 'Food'
  | 'Produce' | 'Packaging' | 'Cleaning Supplies' | 'Operating Supplies';

/** Canonical size unit. Matches backend SizeUnit enum. */
export type SizeUnit = 'ml' | 'cl' | 'l' | 'g' | 'kg' | 'unit';

export type InventoryItemSource = 'MANUAL' | 'IMPORT' | 'AUTO_FROM_RECEIVING';

export interface InventoryItem {
  _id: string;
  /** Optional wine catalogue link. Present on WINE-type items only. */
  wineId?: string;
  venueId: string;
  /** @deprecated Removed from item identity in Phase 1. Not present on new items. */
  locationId?: string;
  /** Canonical display string — always present, includes size (e.g. "Château Margaux 2020 750 ml"). */
  displayName: string;
  /** Behavioral type — drives system rules. */
  type: InventoryItemType;
  /** Optional metadata category — no behavior attached. */
  category?: InventoryItemCategory | null;
  /** Numeric size of this stock unit (e.g. 750). */
  sizeValue?: number;
  /** Unit for sizeValue. */
  sizeUnit?: SizeUnit;
  parLevel?: number;
  reorderPoint?: number;
  source: InventoryItemSource;
  active: boolean;
  /** Supplier memory — written only from goods receipt posting. */
  lastSupplierId?: string;
  lastSupplierName?: string;
  lastReceivedDate?: string; // ISO date
  /** Preferred supplier name joined from SupplierItemLink — returned by list endpoint. */
  preferredSupplierName?: string | null;
  /** Physical bin / shelf location within the venue. */
  binLocation?: string;
  /** Phase A: true = stock movements must reference a named lot for this item. */
  lotTracked: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── InventoryMovement ─────────────────────────────────────────────────────────

export type MovementType =
  | 'RECEIVE'
  | 'DEPLETE'
  | 'TRANSFER_IN'
  | 'TRANSFER_OUT'
  | 'COUNT_VARIANCE'
  | 'MANUAL_ADJUSTMENT'
  | 'WASTE'
  | 'RETURN_TO_SUPPLIER'
  | 'SALE'
  | 'SALE_REVERSAL'
  | 'PRODUCTION_CONSUMPTION' // Phase E: ingredient depletion into production batch
  | 'PRODUCTION_OUTPUT';     // Phase E: finished goods output from production batch

export type MovementSourceDocumentType =
  | 'PURCHASE_ORDER'
  | 'GOODS_RECEIPT'
  | 'STOCK_COUNT'
  | 'TRANSFER'
  | 'QUICK_RECEIVE'
  | 'MANUAL';

export interface MovementSourceDocument {
  type: MovementSourceDocumentType;
  id: string;
}

/**
 * Append-only ledger. No PUT or DELETE.
 * positive quantity = stock in, negative = stock out.
 */
export interface InventoryMovement {
  _id: string;
  inventoryItemId: string;
  /** @deprecated Present on legacy wine-first movements only. */
  wineId?: string;
  venueId: string;
  /** Location this movement was posted against. Present when locationId was recorded. */
  locationId?: string;
  type: MovementType;
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  sourceDocument?: MovementSourceDocument;
  notes?: string;
  postedBy: string;
  postedAt: string;
  createdAt: string;
}

// ── InventoryBalance ──────────────────────────────────────────────────────────

export interface InventoryLotBreakdownEntry {
  lotId: string | null;
  lotNumber: string | null;
  unitCostAtReceipt: number | null;
  quantity: number;
  lastCost?: number | null;
  lastMovementId?: string | null;
  lastMovementType?: MovementType | null;
  lastUpdated?: string;
}

/**
 * Derived/cached read model. Never written directly by user action.
 * Rebuilt from InventoryMovement ledger.
 * Phase A identity: (inventoryItemId, locationId, lotId) — one document per item+location+lot.
 * lastCost is display-only; real cost history lives in InventoryMovement/PurchaseOrderLine.
 */
export interface InventoryBalance {
  _id: string;
  inventoryItemId: string;
  /** @deprecated Present on legacy wine-first balances only. */
  wineId?: string;
  venueId: string;
  /** The location this balance document belongs to. */
  locationId?: string;
  /** Phase A canonical identity field 3. null = null-lot bucket. */
  lotId?: string | null;
  quantity: number;
  lastCost?: number; // display/cache only — not canonical
  lastMovementId?: string;
  lastMovementType?: MovementType;
  lastUpdated: string;
}

// ── Supplier ──────────────────────────────────────────────────────────────────

export type SupplierType =
  | 'DISTRIBUTOR'
  | 'DIRECT_IMPORT'
  | 'AUCTION'
  | 'MERCHANT'
  | 'OTHER';

export interface Supplier {
  _id: string;
  name: string;
  shortName?: string;
  type: SupplierType;
  country?: string;
  accountReference?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  notes?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── SupplierWineLink ──────────────────────────────────────────────────────────

export interface SupplierWineLink {
  _id: string;
  supplierId: string;
  wineId: string;
  /** Denormalised for rendering */
  wineDisplay?: {
    name: string;
    displayVintage: string;
    producerName?: string;
  };
  supplierReference?: string;
  listPrice?: number;
  currency?: string;
  minimumOrderQty?: number;
  leadTimeDays?: number;
  preferredSupplier: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── PurchaseOrder ─────────────────────────────────────────────────────────────

export type PurchaseOrderStatus =
  | 'draft'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'closed'
  | 'cancelled';

export interface PurchaseOrder {
  _id: string;
  venueId: string;
  supplierId: string;
  status: PurchaseOrderStatus;
  expectedDeliveryDate?: string;
  orderedAt?: string;
  notes?: string;
  /** Present only on auto-created POs. Absent on manually created ones. */
  source?: 'QUICK_RECEIVE';
  createdAt: string;
  updatedAt: string;
  /** Only present on the detail endpoint response. */
  invoices?: Invoice[];
}

// ── PurchaseOrderLine ─────────────────────────────────────────────────────────

export interface PurchaseOrderLine {
  _id: string;
  purchaseOrderId: string;
  inventoryItemId: string;
  orderedQty: number;
  unitPrice?: number;
  displayName: string;
  bottleSize?: number;
  /** Server-generated snapshot of the location name at line creation time. */
  locationName: string;
  /** Supplier's own code for this item, joined at response time from SupplierItemLink. */
  supplierItemCode?: string;
  createdAt: string;
  updatedAt: string;
}

// ── OwnershipAccount ──────────────────────────────────────────────────────────

export type OwnershipAccountType = 'VENUE_OWNED' | 'CONSIGNED' | 'EXTERNAL_ENTITY';

export interface OwnershipAccount {
  _id: string;
  venueId: string;
  name: string;
  type: OwnershipAccountType;
  supplierId: string | null;
  isVenueDefault: boolean;
  active: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Ownership Report response shapes ─────────────────────────────────────────

export interface StockByOwnerItem {
  inventoryItemId: string;
  displayName: string;
  quantity: number;
}

export interface StockByOwnerEntry {
  ownershipAccountId: string | null;
  accountName: string;
  accountType: OwnershipAccountType;
  totalQuantity: number;
  items: StockByOwnerItem[];
}

export interface StockByConsignorEntry {
  supplierId: string;
  supplierName: string;
  totalQuantity: number;
  accounts: Array<{
    ownershipAccountId: string;
    accountName: string;
    quantity: number;
  }>;
}

export interface MovementsByOwnerRow {
  movementId: string;
  inventoryItemId: string;
  displayName: string;
  type: string;
  quantity: number;
  locationId: string;
  locationName: string;
  postedAt: string;
  ownershipAccountId: string | null;
  accountName: string;
}

export interface MovementsByOwnerResponse {
  total: number;
  limit: number;
  movements: MovementsByOwnerRow[];
}

export interface ConsignmentSummaryEntry {
  ownershipAccountId: string;
  accountName: string;
  supplierId: string | null;
  supplierName: string | null;
  lotsHeld: number;
  totalQuantity: number;
  movementSummary?: {
    totalIn: number;
    totalOut: number;
  };
}

export interface ConsignmentSummaryResponse {
  accounts: ConsignmentSummaryEntry[];
  dataLimitation?: string;
}

// ── ConsignmentReconciliation ─────────────────────────────────────────────────

export interface ConsignmentReconciliationItem {
  inventoryItemId: string;
  displayName: string | null;
  sizeValue: number | null;
  sizeUnit: string | null;
  locationId: string;
  locationName: string | null;
  lotId: string | null;
  lotNumber: string | null;
  receivedDate: string | null;
  expiryDate: string | null;
  supplierBatchRef: string | null;
  unitCostAtReceipt: number | null;
  quantity: number;
}

export interface ConsignmentReconciliationMovementRow {
  movementId: string;
  type: string;
  quantity: number;
  unitCost: number | null;
  postedAt: string;
  postedBy: string;
  inventoryItem: { _id: string; displayName: string | null; sizeValue: number | null; sizeUnit: string | null };
  location: { _id: string; name: string | null } | null;
  lot: { lotId: string; lotNumber: string | null; supplierBatchRef: string | null } | null;
  sourceDocument: { type: string; id: string } | null;
  notes: string | null;
}

export interface ConsignmentReconciliationResponse {
  header: {
    venueId: string;
    ownershipAccount: { _id: string; name: string; type: string };
    supplier: { _id: string; name: string; shortName: string | null; type: string } | null;
    periodFrom: string;
    periodTo: string;
    generatedAt: string;
    reportType: 'RECONCILIATION';
    dataLimitation: string;
  };
  openingStock: {
    derivedFrom: string;
    totalQuantity: number;
    items: ConsignmentReconciliationItem[];
  };
  movementsInPeriod: {
    totalIn: number;
    totalOut: number;
    byType: Record<string, number>;
    totalMovements: number;
    truncated: boolean;
    rows: ConsignmentReconciliationMovementRow[];
  };
  closingStock: {
    derivedFrom: string;
    totalQuantity: number;
    items: ConsignmentReconciliationItem[];
  };
  reconciliationCheck: {
    reconciled: boolean;
    variance: number;
  };
  payableEstimate: {
    status: 'NOT_AVAILABLE';
    reason: string;
    partialCostBasis: number | null;
  };
}

// ── ConsignmentStatement ──────────────────────────────────────────────────────

export type ExportFormat = 'PDF' | 'XLSX';

export interface ConsignmentStatement {
  _id: string;
  venueId: string;
  ownershipAccountId: string;
  supplierId: string | null;
  statementType: 'RECONCILIATION';
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  generatedBy: string;
  openingTotal: number;
  stockIn: number;
  stockOut: number;
  closingTotal: number;
  reconciled: boolean;
  dataLimitation: string;
  exportFormatLastUsed: ExportFormat;
  notes?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStatementPayload {
  ownershipAccountId: string;
  supplierId?: string | null;
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  openingTotal: number;
  stockIn: number;
  stockOut: number;
  closingTotal: number;
  reconciled: boolean;
  dataLimitation: string;
  exportFormatLastUsed: ExportFormat;
  notes?: string;
}

// ── GoodsReceipt ──────────────────────────────────────────────────────────────

export interface GoodsReceipt {
  _id: string;
  purchaseOrderId: string;
  venueId: string;
  /** The location where stock is received into. Required before posting. */
  locationId?: string;
  ownershipAccountId?: string;
  receivedDate: string;
  receivedBy: string;
  notes?: string;
  postedAt?: string | null;
  /** Present only on auto-created receipts. Absent on manually created ones. */
  source?: 'QUICK_RECEIVE';
  createdAt: string;
  updatedAt: string;
}

export interface GoodsReceiptLine {
  _id: string;
  goodsReceiptId: string;
  purchaseOrderLineId: string;
  inventoryItemId: string;
  receivedQty: number;
  unitCostAtReceipt?: number;
  displayName: string;
  locationName: string;
  /** Phase A: lot number assigned at receipt. Present only when item is lot-tracked. */
  lotNumber?: string;
  /** Phase A: supplier's batch reference. Optional even for lot-tracked items. */
  supplierBatchRef?: string;
  /** Phase A: ISO date string for lot expiry. Optional. */
  expiryDate?: string;
  createdAt: string;
}

// ── Invoice ───────────────────────────────────────────────────────────────────

export type InvoiceStatus = 'draft' | 'open' | 'void';
export type InvoiceExtractionStatus = 'pending' | 'completed' | 'failed';

export interface InvoiceExtractedData {
  invoiceNumber?: string;
  supplierName?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalAmount?: number;
  currency?: string;
}

export interface InvoiceAttachment {
  _id: string;
  originalName: string;
  storageKey: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface Invoice {
  _id: string;
  venueId: string;
  supplierId: string;
  invoiceNumber: string;
  purchaseOrderId?: string | null;
  goodsReceiptIds: string[];
  invoiceDate?: string | null;
  dueDate?: string | null;
  subtotalAmount?: number | null;
  taxAmount?: number | null;
  totalAmount?: number | null;
  currencyCode?: string | null;
  status: InvoiceStatus;
  attachments: InvoiceAttachment[];
  extractionStatus?: InvoiceExtractionStatus | null;
  extractedData?: InvoiceExtractedData | null;
  extractionRawText?: string | null;
  extractionError?: string | null;
  notes?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── StockCount ────────────────────────────────────────────────────────────────

export type StockCountStatus = 'draft' | 'posted';

export interface StockCount {
  _id: string;
  venueId: string;
  /** null = no-location (venue-level) count */
  locationId: string | null;
  status: StockCountStatus;
  /** When the physical count happened — editable in draft */
  countedAt: string | null;
  /** Auto-set when posted — not editable */
  postedAt: string | null;
  countedBy: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ── StockCountLine ────────────────────────────────────────────────────────────

export interface StockCountLine {
  _id: string;
  stockCountId: string;
  inventoryItemId: string;
  /** Frozen at line creation from InventoryBalance.quantity */
  expectedQty: number;
  /** null until the user enters a count */
  countedQty: number | null;
  /** Derived: countedQty - expectedQty, null until countedQty is set */
  varianceQty: number | null;
  snapshot: {
    displayName: string;
    bottleSize: number | null;
    locationName: string | null;
    lotTracked?: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

// ── InventoryDashboard ────────────────────────────────────────────────────────

/** A single enriched movement row as returned by the dashboard endpoint. */
export interface DashboardMovementRow {
  inventoryItemId: string;
  displayName: string | null;
  displayVintage: string | null;
  bottleSize: number | null;
  locationName: string | null;
  type: MovementType;
  quantity: number;
  postedAt: string;
  sourceDocument?: { type: string; id: string } | null;
}

export interface InventoryDashboard {
  totalItems: number;
  totalBottles: number;
  lowStockCount: number;
  outOfStockCount: number;
  recentlyReceived: {
    count: number;
    items: DashboardMovementRow[];
  };
  recentMovements: DashboardMovementRow[];
}

// ── InventoryValuation ────────────────────────────────────────────────────────

export interface InventoryValuationItem {
  inventoryItemId: string;
  displayName: string | null;
  locationName: string | null;
  quantity: number;
  lastCost: number;
  lineValue: number;
}

export interface InventoryValuation {
  totalValue: number;
  currency: string | null;
  itemCount: number;
  unvaluedCount: number;
  asOf: string;
  disclaimer: string;
  items: InventoryValuationItem[];
}

// ── MovementFeedRow ───────────────────────────────────────────────────────────

/** Enriched row returned by GET /admin/venue/:venueId/inventory/movement/feed */
export interface MovementFeedRow {
  _id: string;
  inventoryItemId: string;
  wineId: string;
  venueId: string;
  locationId: string | null;
  displayName: string | null;
  displayVintage: string | null;
  producerName: string | null;
  locationName: string | null;
  type: MovementType;
  quantity: number;
  unitCost: number | null;
  totalCost: number | null;
  notes: string | null;
  sourceDocument: { type: string; id: string } | null;
  postedBy: string;
  postedAt: string;
  createdAt: string;
}

// ── Transfer ──────────────────────────────────────────────────────────────────

export type TransferStatus = 'draft' | 'posted';

export interface Transfer {
  _id: string;
  venueId: string;
  /** null = no-location (venue-level) scope */
  fromLocationId: string | null;
  /** null = no-location (venue-level) scope */
  toLocationId: string | null;
  status: TransferStatus;
  notes?: string;
  /** When the physical transfer happened — editable in draft */
  transferredAt: string | null;
  /** Who performed the transfer — editable in draft */
  transferredBy: string;
  /** Auto-set when posted — not editable */
  postedAt: string | null;
  /** Auto-set when posted — not editable */
  postedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── TransferLine ──────────────────────────────────────────────────────────────

export interface TransferLine {
  _id: string;
  transferId: string;
  fromInventoryItemId: string;
  quantity: number;
  /** Phase A: lot being transferred. null = null-lot bucket. */
  lotId?: string | null;
  snapshot: {
    displayName: string;
    bottleSize: number | null;
    fromLocationName: string | null;
    toLocationName: string | null;
  };
  /** Balance at line creation — returned by API for UI display, not persisted */
  currentQty?: number;
  createdAt: string;
  updatedAt: string;
}

// ── Recipe ────────────────────────────────────────────────────────────────────

export interface Recipe {
  _id: string;
  venueId: string;
  name: string;
  outputInventoryItemId: string;
  outputQuantity: number;
  yieldPercent?: number | null;
  version: number;
  active: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeLine {
  _id: string;
  recipeId: string;
  venueId: string;
  inventoryItemId: string;
  /** Ingredient display name — enriched by the server; absent on legacy responses. */
  displayName?: string | null;
  quantity: number;
  sizeUnit?: SizeUnit | null;
  position: number;
  notes?: string | null;
  createdAt: string;
}

// ── ProductionOrder ───────────────────────────────────────────────────────────

export type ProductionOrderStatus = 'DRAFT' | 'POSTED';

export interface ProductionOrder {
  _id: string;
  venueId: string;
  recipeId: string;
  recipeVersion: number;
  outputInventoryItemId: string;
  plannedQuantity: number;
  locationId: string;
  outputOwnershipAccountId: string;
  status: ProductionOrderStatus;
  postedAt?: string | null;
  postedBy?: string | null;
  plannedDate?: string | null;
  outputLotId?: string | null;
  outputExpiryDate?: string | null;
  outputLotNumber?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductionOrderLine {
  _id: string;
  productionOrderId: string;
  venueId: string;
  inventoryItemId: string;
  requiredQuantity: number;
  fromLocationId: string;
  sourceOwnershipAccountId: string;
  lotId?: string | null;
  snapshot: {
    displayName: string;
    sizeValue?: number | null;
    sizeUnit?: SizeUnit | null;
    /** Original recipe measure before UOM conversion (e.g. 200). Absent on legacy orders. */
    recipeQuantity?: number | null;
    /** Unit of the original recipe measure (e.g. 'ml', 'g'). Absent on legacy orders. */
    recipeSizeUnit?: SizeUnit | null;
  };
  position: number;
  createdAt: string;
}

// ── ProductionAvailability ────────────────────────────────────────────────────

export interface ProductionLineAvailability {
  productionOrderLineId: string;
  inventoryItemId: string;
  displayName: string;
  requiredQuantity: number;
  availableQuantity: number;
  shortfallQuantity: number;
  isAvailable: boolean;
  fromLocationId: string;
  sourceOwnershipAccountId: string;
  lotId: string | null;
  requiresLotSelection: boolean;
  message?: string;
}

export interface ProductionOrderAvailability {
  orderId: string;
  status: ProductionOrderStatus;
  canProduce: boolean;
  recipeVersionDrift: boolean;
  lines: ProductionLineAvailability[];
}

// ── ProductionPostingResult ───────────────────────────────────────────────────

export interface ProductionPostingResult {
  order: ProductionOrder;
  outputLot: InventoryLot | null;
  consumptionMovements: InventoryMovement[];
  outputMovement: InventoryMovement;
  availabilityWarning?: string;
}
